import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { toast } from "react-toastify";

import { tokenStorage } from "./tokenStorage";
import { API_ENDPOINTS } from "../constants/api";

// ─── Axios instance ──────────────────────────────────────────────────────────
const BASE_URL = process.env.REACT_APP_BASE_URL || "/api";

const instance = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
    "Access-Control-Allow-Origin": "*",
    ClientDb: process.env.REACT_APP_CLIENT_DB || "",
    ENVTYPE: `${process.env.REACT_APP_ENV}-${process.env.REACT_APP_CLIENT_DB}`,
    Accept: "*/*",
  },
  timeout: 15 * 60 * 1000,
});

// instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
//   if (!config.url) return config;

//   console.log(config.url, config.baseURL)
//   const currentUrl = new URL(config.url, config.baseURL);

//   Object.entries(config.urlParams || {}).forEach(([key, value]) => {
//     currentUrl.pathname = currentUrl.pathname.replace(
//       `:${key}`,
//       encodeURIComponent(value as string),
//     );
//   });

//   const authPart =
//     currentUrl.username && currentUrl.password
//       ? `${currentUrl.username}:${currentUrl.password}`
//       : "";

//   return {
//     ...config,
//     baseURL: config.baseURL,
//     url: currentUrl.pathname,
//   };
// });

// ─── Interceptor 1: resolve path params (:id, :slug …) ───────────────────────
instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (!config.url) return config;
  let url = config.url;

  // Replace path params (:id, :slug, etc.)
  if (config.urlParams) {
    Object.entries(config.urlParams).forEach(([key, value]) => {
      url = url.replace(`:${key}`, encodeURIComponent(String(value)));
    });
  }

  return {
    ...config,
    url,
  };
});

// ─── Interceptor 2: attach Bearer token from tokenStorage ────────────────────
instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStorage.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─── Interceptor 3: handle 401 — refresh token → retry ──────────────────────

// Queue of failed requests waiting for a token refresh to complete
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null): void => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token!)));
  failedQueue = [];
};

function extractApiMessage(error: AxiosError): void {
  const data = error.response?.data as Record<string, unknown> | undefined;
  if (!data?.detail) return;

  if (typeof data.detail === "string") {
    error.message = data.detail;
  } else if (Array.isArray(data.detail)) {
    const msgs = data.detail
      .map((d: { msg?: string }) => d.msg)
      .filter(Boolean);
    if (msgs.length) error.message = msgs.join("; ");
  }
}

/**
 * A deactivated account can get rejected by any endpoint under different
 * status codes (confirmed to vary — 401/403/404 depending on which one),
 * so this matches on the `detail` text itself instead of a status code.
 * Returns the backend's own message when matched, otherwise null.
 */
function accountDeactivatedMessage(error: AxiosError): string | null {
  const data = error.response?.data as Record<string, unknown> | undefined;
  const detail = data?.detail;
  return typeof detail === "string" && /deactivat/i.test(detail)
    ? detail
    : null;
}

instance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Account deactivated mid-session — end it right away, ahead of every
    // other check below (status code, retry flag, excluded URLs). No
    // status-code branch here on purpose (see accountDeactivatedMessage):
    // retrying with a refreshed token can't fix a deactivated account, so
    // there's nothing worth queuing/retrying regardless of which status
    // code carried this. Toasted here — not left to each hook's own
    // onError — because that's the only way "every API" is covered
    // without auditing every hook in the app for this one message.
    const deactivatedMessage = accountDeactivatedMessage(error);
    if (deactivatedMessage) {
      toast.error(deactivatedMessage);
      tokenStorage.clearTokens();
      window.dispatchEvent(new CustomEvent("auth:unauthorized"));

      // Strip `detail` before rejecting so the hook whose call actually
      // failed doesn't ALSO toast this same message in its own onError —
      // most onError handlers in this app key off `detail` to decide what
      // to show, so this one toast above is deliberately the only one.
      if (
        error.response &&
        error.response.data &&
        typeof error.response.data === "object"
      ) {
        delete (error.response.data as Record<string, unknown>).detail;
      }
      return Promise.reject(error);
    }

    // Only handle 401 once per request; skip auth endpoints to avoid loops.
    // CHANGE_PASSWORD is skipped too: its 401 means "current password is
    // wrong" (a normal, recoverable form error per the API spec), not an
    // expired session — running it through refresh-and-retry was masking
    // that message behind a refresh call, which surfaced as a generic
    // "network error" (and a forced logout) whenever the refresh itself
    // failed, instead of the real "current password is incorrect" toast.
    if (
      error.response?.status !== 401 ||
      original._retry ||
      original.url?.includes(API_ENDPOINTS.AUTH.REFRESH) ||
      original.url?.includes(API_ENDPOINTS.AUTH.LOGIN) ||
      original.url?.includes(API_ENDPOINTS.AUTH.FORGOT_PASSWORD) ||
      original.url?.includes(API_ENDPOINTS.AUTH.RESET_PASSWORD) ||
      original.url?.includes(API_ENDPOINTS.AUTH.CHANGE_PASSWORD)
    ) {
      extractApiMessage(error);
      return Promise.reject(error);
    }

    // If a refresh is already in-flight, queue this request
    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          original.headers.Authorization = `Bearer ${token}`;
          return instance(original);
        })
        .catch((err) => Promise.reject(err));
    }

    original._retry = true;
    isRefreshing = true;

    const refreshToken = tokenStorage.getRefreshToken();

    if (!refreshToken) {
      isRefreshing = false;
      window.dispatchEvent(new CustomEvent("auth:unauthorized"));
      return Promise.reject(error);
    }

    try {
      // Use a plain axios call (not the instance) to avoid interceptor loops.
      // `baseURL` in the config, NOT string-concatenated into the url, so this
      // gets the exact same robust joining `instance` already gets. A
      // hand-concatenated `${BASE_URL}${url}` broke the moment BASE_URL was a
      // bare "/" (REACT_APP_BASE_URL=/): "/" + "/auth/refresh" = "//auth/refresh",
      // and a URL starting with `//` is PROTOCOL-RELATIVE -- the browser reads
      // everything between the `//` and the next `/` as the hostname, so this
      // was resolving to a real request for a server literally named "auth"
      // (net::ERR_NAME_NOT_RESOLVED). axios's own baseURL+url joining collapses
      // that double slash correctly instead of producing it.
      const { data } = await axios.post(
        API_ENDPOINTS.AUTH.REFRESH,
        { refresh_token: refreshToken },
        {
          baseURL: BASE_URL,
          headers: {
            "Content-Type": "application/json",
            ClientDb: process.env.REACT_APP_CLIENT_DB || "",
          },
        },
      );

      const newAccessToken: string = data.access_token;
      const newRefreshToken: string | undefined = data.refresh_token;
      const newExpiresIn: number = data.expires_in ?? 7200;
      const newRefreshTokenExpiresIn: number | undefined =
        data.refresh_token_expires_in;

      // Persist updated tokens in cookies with correct expiry
      tokenStorage.setAccessToken(newAccessToken, newExpiresIn);
      if (newRefreshToken && newRefreshTokenExpiresIn) {
        tokenStorage.setRefreshToken(newRefreshToken, newRefreshTokenExpiresIn);
      }

      // Notify Zustand store so in-memory state stays in sync
      window.dispatchEvent(
        new CustomEvent("auth:token-refreshed", {
          detail: {
            accessToken: newAccessToken,
            refreshToken: newRefreshToken,
            expiresIn: newExpiresIn,
            refreshTokenExpiresIn: newRefreshTokenExpiresIn,
          },
        }),
      );

      processQueue(null, newAccessToken);
      isRefreshing = false;

      original.headers.Authorization = `Bearer ${newAccessToken}`;
      return instance(original);
    } catch (refreshError) {
      processQueue(refreshError, null);
      isRefreshing = false;
      tokenStorage.clearTokens();
      window.dispatchEvent(new CustomEvent("auth:unauthorized"));
      return Promise.reject(refreshError);
    }
  },
);

export default instance;
